import type React from 'react'
import { useCallback } from 'react'
import { useStableCallbacks } from '@/hooks/use-stable-callbacks'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import type { ShapeManagerRef } from '@/lib/shapes/types/shape-manager-ref'

import { isExactRotationSnapGuideDegree } from '@/lib/guides/smart-guides'
import { clampRotationSliderDegrees, rotationForShapeState } from '../helpers/rotation-degrees'
import type { PanelFieldErrors } from '../types/panel-fields'
import type { SingleRotateBefore } from '../types/single-rotate-before'

/**
 * Event handlers for the Rotate controls: presets, slider drag/commit and the degree text field.
 * Stable across renders, so the memoized `RotationControl` skips renders that don't change it.
 */
export function useRotationInputHandlers(params: {
  activeObject: ShapeData | null
  activeObjects: ShapeData[]
  isMultiSelect: boolean
  shapeManagerRef?: React.RefObject<ShapeManagerRef | null>
  showGuides: boolean
  panelControlsDisabled: boolean
  rotateControlDisabled: boolean
  rotationRef: React.MutableRefObject<number[]>
  panelRotationSliderActiveRef: React.MutableRefObject<boolean>
  panelSingleRotateBeforeRef: React.MutableRefObject<SingleRotateBefore | null>
  rotationEditStartValueRef: React.MutableRefObject<number | null>
  applyRotationToSelection: (degrees: number) => number | null
  commitRotation: (degrees: number) => void
  syncRotationDisplay: (deg: number) => void
  setRotationText: React.Dispatch<React.SetStateAction<string>>
  setFieldErrors: React.Dispatch<React.SetStateAction<PanelFieldErrors>>
}) {
  const {
    activeObject,
    activeObjects,
    isMultiSelect,
    shapeManagerRef,
    showGuides,
    panelControlsDisabled,
    rotateControlDisabled,
    rotationRef,
    panelRotationSliderActiveRef,
    panelSingleRotateBeforeRef,
    rotationEditStartValueRef,
    applyRotationToSelection,
    commitRotation,
    syncRotationDisplay,
    setRotationText,
    setFieldErrors,
  } = params

  const handlePresetChange = (val: string) => {
    if (!val) return
    const d = parseInt(val, 10)

    if (!Number.isFinite(d)) return
    commitRotation(d)
  }

  const handleSliderPointerDown = () => {
    if (panelControlsDisabled || rotateControlDisabled) return
    if (isMultiSelect) {
      panelRotationSliderActiveRef.current = true
      if (activeObjects.length >= 2) {
        shapeManagerRef?.current?.beginPanelMultiSelectRotation()
      }
    } else if (activeObject) {
      panelSingleRotateBeforeRef.current = {
        kind: 'shape',
        id: activeObject.id,
        rotation: rotationForShapeState(activeObject.rotation ?? 0),
      }
    }
  }

  /** Pointer up or cancel on the slider. */
  const handleSliderPointerEnd = () => {
    panelRotationSliderActiveRef.current = false
    shapeManagerRef?.current?.updatePanelRotationGuidePreview(null)
  }

  const handleSliderValueChange = (v: number[]) => {
    const raw = clampRotationSliderDegrees(v[0] ?? 0)
    const norm = rotationForShapeState(raw)
    const mgr = shapeManagerRef?.current

    // Exact slider angle always drives geometry; guides only when on a snap axis (no magnetic jump).
    if (showGuides && isExactRotationSnapGuideDegree(norm)) {
      mgr?.updatePanelRotationGuidePreview(norm)
    } else {
      mgr?.updatePanelRotationGuidePreview(null)
    }

    const applied = applyRotationToSelection(norm)

    // Shows the applied angle, or the slider's own angle when nothing could be rotated.
    syncRotationDisplay(applied ?? norm)
  }

  const handleSliderValueCommit = (v: number[]) => {
    shapeManagerRef?.current?.updatePanelRotationGuidePreview(null)
    const raw = clampRotationSliderDegrees(v[0] ?? 0)
    const norm = rotationForShapeState(raw)

    commitRotation(norm)
  }

  const handleRotationFocus = useCallback(() => {
    rotationEditStartValueRef.current = rotationRef.current[0] ?? 0
  }, [rotationEditStartValueRef, rotationRef])

  const handleRotationTextChange = useCallback(
    (raw: string) => {
      setRotationText(raw)
      setFieldErrors((prev) => ({ ...prev, rotation: undefined }))
    },
    [setRotationText, setFieldErrors]
  )

  return useStableCallbacks({
    handlePresetChange,
    handleSliderPointerDown,
    handleSliderPointerEnd,
    handleSliderValueChange,
    handleSliderValueCommit,
    handleRotationFocus,
    handleRotationTextChange,
  })
}
